// SimContext — the per-game home of every C++ file-scope global the sim touches.
// One instance per simulated game; NO module-level mutable state anywhere in the sim
// (the DO re-sims multiple games in one isolate). Fields grow as porting proceeds;
// each is anchored to the global it replaces.

import { Rng } from './math/rng.ts';
import { knVersionSimulation } from './simversion.ts';
import { Dictionary } from './data/dictionary.ts';
import { CommandQueue } from './sm/commandqueue.ts';
import { StateFrame } from './hash/stateframe.ts';
import { StateTracker } from './hash/statetracker.ts';
import { GobMgr } from './gob/gobmgr.ts';
import type { PlayerLike } from './gob/gob.ts';
import { Player, PlayerMgr } from './player/player.ts';
import type { StateMachineMgr } from './sm/statemachine.ts';

/** The sim's tick clock (the TimerMgr the C++ passes to gsmm.Init). Sim time advances
 *  kctUpdate(=8) ticks per update inside Simulation::Update — never wall clock. */
export interface TickSource {
  GetTickCount(): number;
}

export class SimContext {
  /** game/misc.cpp:581 glSeed — the lockstep-synchronized RNG. */
  rng = new Rng();
  /** game/misc.cpp:610 glAsyncSeed — local-only cosmetics; NEVER sim-affecting. */
  rngAsync = new Rng();

  /** NON-C++. When true, the SaveState/LoadState methods emit/read the COMPLETE TS resume
   *  format instead of the byte-faithful C++ savegame: gobs tail-first (no load-reversal),
   *  m_ff assigned (no ctor-default re-OR), dummy owner via kpidNeutral, the free-list order,
   *  an always-present builder rally, the raw BuildQueue, and the omitted player economy
   *  accumulators. Set ONLY by Sim.snapshot()/restore() (try/finally), so saveState()/load()
   *  stay byte-identical to the shipping C++. This is what makes a loaded sim continue a
   *  from-scratch trajectory bit-exact (issue #96); the C++ format cannot, by design
   *  (sim-quirks B1/B2/B3/B22 + the uncatalogued free-list/rally/accumulator omissions). */
  snapshotComplete = false;

  /** game/game.cpp gfMultiplayer (via kfGameMultiplayer). */
  fMultiplayer = false;

  /** The lockstep sim-behavior version this game/re-sim runs AS (GameParams.dwVersionSimulation /
   *  a replay's header simVersion). Version-gated behavior branches on it so old recordings replay
   *  with their original behavior. Defaults to the current knVersionSimulation; Sim.create + the
   *  replay/corpus harnesses set it from the recording. See tasks/sim-quirks.md. */
  simVersion = knVersionSimulation;

  /** Resolves the `$iphone` trigger sysvar (gamevar.ts): true when the LOCAL
   *  player is using the touch FingerHandler (the multitouch UI), false on the
   *  mouse StylusHandler. The client mirrors gfMouseMode into this via
   *  SimUIForm.SetUIType so $iphone-gated tutorial ecoms pick the device-correct
   *  variant. Defaults false (mouse) so option-less harnesses keep $iphone=0. */
  fTouchControls = false;

  /** game/SimUI.cpp:27 gcMessagesPerUpdate. */
  cMessagesPerUpdate = 0;

  /** game/SimUI.cpp:847 gpframeCurrent — non-null while inside a tracked update. */
  frameCurrent: StateFrame | null = null;

  /** SimUIForm::m_ptracker (game/SimUI.cpp:180). */
  tracker = new StateTracker(0);

  /** game/GameObjects.cpp ggobm. */
  gobm = new GobMgr(this);

  /** game/Player.cpp:10 gplrm. */
  plrm = new PlayerMgr(this);

  /** game/Player.cpp:11 gpplrLocal — the LOCAL player (machine-local perspective; for
   *  re-sim equivalence this is the player whose client captured the oracle frames). */
  plrLocal: Player | null = null;

  /** game/Player.cpp:12 gplrDummy — gobs' owner before SetOwner (a real zeroed Player). */
  plrDummy: PlayerLike = new Player(this);

  /** game/StateMachine.cpp gsmm — bound during Sim assembly (GobStateMachineMgr). */
  smm: StateMachineMgr | null = null;

  /** game/ht.h:8556 gapuntc — UnitConsts by kut (um/wf masks); filled from templates
   *  JSON at sim init (full UnitConsts land with the Unit.cpp port). */
  unitConsts: import('./gob/unitconsts.ts').UnitConsts[] = [];

  /** game/Simulation.cpp gsim — bound during Sim assembly. */
  sim: {
    GetUpdateCount(): number;
    GetTickCount(): number;
    GetBuildMgr(): { OnBuilt?(punt: unknown, pbldr: unknown): void } | null;
    /** gsim.GetLevel() — the per-game Level (LevelLike; the trigger/gob code reads its
     *  TriggerMgr/UnitGroupMgr/Version through it). */
    GetLevel(): import('./sim/simulation.ts').LevelLike | null;
  } | null = null;

  /** gsim.GetLevel()->GetTerrainMap() — Level lands M3; until then the sim assembler
   *  binds the TerrainMap here directly. */
  trmap: import('./terrain/terrainmap.ts').TerrainMap | null = null;

  /** gsim.GetLevel()->GetFogMap() — bound by Level.LoadLevelConstants. */
  fogm: import('./sim/fogmap.ts').FogMap | null = null;

  // (The one-update trigger-condition seam that used to live here was removed: its
  // no-op default silently dropped notifications TWICE — once on the create path,
  // once on the load path. Miner/Processor now call the TriggerMgr directly through
  // sim.GetLevel(), the C++ shape, so the bug class is unrepresentable.)

  /** game/MobileUnit.cpp:2492 s_nSeqMoveAside — the move-aside visit sequence (word). */
  nSeqMoveAside = 0;

  /** game/GameObjects.cpp:672 gnSequenceScorch — scorch age order (oldest eviction). */
  gnSequenceScorch = 0;

  /** game/game.cpp gcStructGobsComputerDeltaSP/gcStructGobsHumanDeltaSP — per-level SP
   *  limit scaling (CalcUnitCountDeltas binds them at SP level load; 0 in MP). */
  cStructGobsComputerDeltaSP = 0;
  cStructGobsHumanDeltaSP = 0;

  /** game/Simulation.cpp:7 gcmdq — the LOCAL command queue. SP: consumed directly by
   *  Simulation::Update. MP: flushed to the server each frame (SimUI.cpp:795-811) and the
   *  sim consumes only the relayed stream — a re-sim driver must Clear() it per update. */
  cmdq = new CommandQueue();

  /** game/ht.h:6811 gfGodMode — debug cheat; pinned false for sim equivalence. */
  fGodMode = false;

  /** ReplicatorGob's static input-point registry (Replicator.cpp statics, M3). The
   *  Replicator port maintains this; MobileUnitGob::CheckReplicatorPoint scans it. */
  /** ReplicatorGob::s_atptInput[10] + s_cReplicators (Replicator.cpp:440) — the raw
   *  static array. Slots >= the count are STALE (RemoveReplicatorInputPoint never
   *  clears the vacated tail) and are SERIALIZED raw, so they must be modeled. */
  replicatorInputPoints: { tx: number; ty: number }[] = Array.from({ length: 10 }, () => ({
    tx: 0,
    ty: 0,
  }));
  cReplicatorInputPoints = 0;

  /** UnitGroupMgr::GetUnitGroup(gid) — bound when UnitGroupMgr ports; replication clones
   *  group membership through it. */
  unitGroupOfGob?: (gid: number) => { AddUnit?: (punt: unknown, f: boolean) => void } | null;

  /** game/Unit.cpp:11-12 — fleet-wide armor extremes, updated by every MobileUnit
   *  InitClass. Max starts at itofx((unsigned)-1): the <<5 overflow makes that -32. */
  gfxMobileUnitArmorStrengthMin = 511 << 5;
  gfxMobileUnitArmorStrengthMax = -32;

  /** game/Unit.cpp:23-24 — fleet-wide move-rate extremes (WCoord). */
  gwcMoveDistPerUpdateMin = 32767;
  gwcMoveDistPerUpdateMax = -1;

  /** gwfPerfOptions — PINNED sim constant (the MP lobby forces kfPerfMax; perf-gated RNG
   *  sites otherwise desync — tasks/sim-core-map.md). */
  wfPerfOptions = 0xffff; // kfPerfMax

  /** AnimationData registry (.anir metadata by name) — bound at level load. */
  animDataFor: (szAniName: string) => import('./anim/animation.ts').AnimData = () => {
    throw new Error('animDataFor unbound — the level loader registers anim data');
  };

  /** The presentation host (SimHost) — every method is fire-and-forget; nothing the sim
   *  branches on. Defaults are no-ops (headless re-sim). */
  host: {
    PlaySfx(sfx: number | string): void;
    CalcPoweredRadar(): void;
    UpdateAllies(side: number, sidm: number): void;
    /** Simulation.cpp:425 ggame.ScheduleUpdateTriggers — the trigger pump EVENT (M3). */
    ScheduleUpdateTriggers(): void;
    /** Simulation.cpp:573-590 — local-player alert seams (wall-clock throttled UI). */
    NotifyBaseUnderAttack(): void;
    NotifyPowerLow(): void;
    /** Processor.cpp:455-466 "Need More Storage!" — local-player gated in
     *  the sim; the 30s wall-clock throttle + alert + sfx are host. */
    NotifyStorageFull(): void;
    /** Miner.cpp:426-431 "Miner Under Attack!" — local-player + once-per-attack
     *  (m_fMinerUnderAttack) gated in the SIM; the sfx + alert are host. */
    NotifyMinerUnderAttack(): void;
    /** Simulation.cpp:608-625 — the disconnect re-route to the game callback (M3). */
    OnPlayerDisconnect(pid: number, nReason: number): void;
    /** Unit.cpp:295-303 — local-player fog reveal (per-client; fogmap lands M3). */
    /** Fog reveal for gpplrLocal at the C++ call-site's tile coords (Unit.cpp:305 and
     *  Struct.cpp:704 use GetCenter; MobileUnit.cpp:3011 uses GetTilePosition — they
     *  differ by the UI-bounds offset, so the SIM computes tx/ty, not the host). */
    RevealFog(gob: unknown, fLarge: boolean, tx: number, ty: number): void;
    /** Unit.cpp:399-403 — close the unit's menu form if it owns it. */
    CloseUnitMenu(gob: unknown): void;
    /** MobileBuilder.cpp:209 — pfrmBuild->DefUpdate(this, IsBuildInProgress())
     *  (the build-order form's progress refresh; forms UI). */
    BuildFormDefUpdate(gob: unknown, fBuildInProgress: boolean): void;
    /** MobileBuilder.cpp:184 — pfrmBuild->OnUnitCompleted(this, ut) (forms UI). */
    BuildFormOnUnitCompleted(gob: unknown, ut: number): void;
    /** Headquarters.cpp:178-186 (HqGob) + MobileBuilder.cpp:71-77 — Deactivate
     *  takes down the build/place forms this builder owns (forms UI). */
    BuilderDeactivate(gob: unknown): void;
    /** Unit.cpp:683 gevm.SetRedrawFlags(kfRedrawBeforeTimer). */
    SetRedrawBeforeTimer(): void;
    /** The gpmm-> minimap-CONTENT recompute reaches: the C++ minimap dib only
     *  updates at these sim mutation points (it is event-driven incremental, NOT
     *  recomputed per frame — SimUI.cpp:2837-2881), so a pixel-faithful client
     *  must recompute exactly here. Tile coords; trc is half-open. Call sites:
     *  fogmap.cpp:180/265/321/577/596, Unit.cpp:316/339/631,
     *  MobileUnit.cpp:1378/3022, LRInfantry.cpp:120, SRInfantry.cpp:119,
     *  Andy.cpp:146, Struct.cpp:189/715. Fire-and-forget; no-ops headless. */
    MiniMapRedraw(): void;
    MiniMapRedrawTile(tx: number, ty: number): void;
    MiniMapRedrawTRect(txLeft: number, tyTop: number, txRight: number, tyBottom: number): void;
    /** Gob::Invalidate() (GameObjects.cpp:267) — render-only: the client damages the
     *  gob's last-painted bounds (m_trcBoundingLast, a render field it stashes on the
     *  gob) so its pixels repaint. Call sites: GobMgr::RemoveGob (GameObjects.cpp:1583)
     *  and MinerGob::Hide both branches (Miner.cpp:121/124 — dock/undock at a
     *  Processor). No-op headless. */
    GobInvalidate(gob: unknown): void;
    /** Trigger-action presentation seams (TriggerActions.cpp; all fire-and-forget). */
    ShowAlert(psz: string): void;
    CenterViewOnArea(nArea: number): void;
    SetNextLevel(pszLevel: string): void;
    /** SP EndMissionAction: objectives screen + gameOverEvent (the sim already
     *  disabled the TriggerMgr). */
    EndMission(fWin: boolean, side: number): void;
    /** MP EndMissionAction: the async mpEndMissionWin/Lose + checkGameOver events. */
    NotifyEndMission(side: number, fWin: boolean): void;
    Ecom(
      nCharFrom: number,
      nCharTo: number,
      pszMessage: string,
      nBackground: number,
      fMore: boolean,
    ): void;
    /** SP-only UI-state READ (TriggerActions.cpp:509 gpmfrmm->EcomSuppressed()): a
     *  build form being up BLOCKS the EcomAction. SP is a single machine, so the C++
     *  reads UI state here too; headless default false. */
    EcomSuppressed(): boolean;
    ShowObjectives(side: number): void;
    CutScene(pszMessage: string): void;
    JumpToMission(pszLevel: string): void;
    UpdateCountdownString(secs: number, pszFormat: string): void;
    ShowCountdownTimer(fShow: boolean): void;
    /** NON-C++ replay/analytics seam: a damage-dealing shot was launched
     *  (TankShotGob::Launch / RocketGob::InitRocket — every kmidHit originates there).
     *  Fire-and-forget like every other host method; the replay viewer's event pass
     *  uses it to find the first battle between each pair of sides. */
    NotifyShotFired(sideAssailant: number, sideTarget: number, wx: number, wy: number): void;
    /** NON-C++ replay/analytics seam: a structure changed hands
     *  (StructGob::Takeover — the takeover-specialist capture). */
    NotifyStructTakeover(
      sideOld: number,
      sideNew: number,
      ut: number,
      wx: number,
      wy: number,
    ): void;
  } = {
    PlaySfx() {},
    CalcPoweredRadar() {},
    UpdateAllies() {},
    ScheduleUpdateTriggers() {},
    NotifyBaseUnderAttack() {},
    NotifyPowerLow() {},
    NotifyStorageFull() {},
    NotifyMinerUnderAttack() {},
    OnPlayerDisconnect() {},
    RevealFog() {},
    CloseUnitMenu() {},
    BuildFormDefUpdate() {},
    BuildFormOnUnitCompleted() {},
    BuilderDeactivate() {},
    SetRedrawBeforeTimer() {},
    MiniMapRedraw() {},
    MiniMapRedrawTile() {},
    MiniMapRedrawTRect() {},
    GobInvalidate() {},
    ShowAlert() {},
    CenterViewOnArea() {},
    SetNextLevel() {},
    EndMission() {},
    NotifyEndMission() {},
    Ecom() {},
    EcomSuppressed() {
      return false;
    },
    ShowObjectives() {},
    CutScene() {},
    JumpToMission() {},
    UpdateCountdownString() {},
    ShowCountdownTimer() {},
    NotifyShotFired() {},
    NotifyStructTakeover() {},
  };

  /** ggame persistent profile variables (Get/SetVar) — SP mission-scripting state read
   *  by TestPvarCondition / written by ModifyPvar/SetPvarTextAction. */
  pvars = new Dictionary(); // MOVE-TO-FRONT, case-insensitive (game/misc.cpp Dictionary)

  /** gpfrmPlace->GetOwner() != NULL (PlaceStructureModeCondition) — the SP
   *  place-structure UI mode; the embedder mirrors the form state here. */
  fPlaceStructureMode = false;

  /** The level-vector source for savegame loads (Level::LoadState re-runs Init by NAME;
   *  the C++ reads the pak — the TS embedder supplies extracted LevelData). */
  levelDataFor?: (szLevel: string) => import('./level.ts').LevelData;
}
